
<lib-base-signal-input
  [label]="label()"
  [hint]="helpText()"
  [showRequired]="showRequiredIndicator()"
  [showError]="displayError()"
  [errorMessage]="displayMessage()"
  [disabled]="disabled()"
  [showExternalLabel]="true"
>
  <div input class="w-full space-y-3 py-2">
    <!-- Mobile card layout -->
    <div
      class="space-y-4"
      [class.hidden]="previewMode() !== 'mobile'"
    >
      @for (row of rows(); track $index; let rowIndex = $index) {
        <section
          class="overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
          <div class="bg-gray-50 px-4 py-3">
            @if (isUserAddedRowIndex(rowIndex)) {
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  class="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  [value]="row.display"
                  [disabled]="disabled() || readonly()"
                  placeholder="Enter item"
                  (input)="updateDisplay(rowIndex, $any($event.target).value)"
                  (blur)="markLocallyTouched()"
                />

                @if (canRemoveRow(rowIndex)) {
                  <button
                    type="button"
                    class="rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-red-600 disabled:opacity-60"
                    [disabled]="disabled() || readonly()"
                    aria-label="Remove row"
                    (click)="removeRow(rowIndex)"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                }
              </div>
            } @else {
              <h3 class="text-sm font-semibold text-gray-900">
                {{ row.display }}
              </h3>
            }
          </div>

          <!-- Choices -->
          <div class="p-3">
            @switch (effectiveType()) {
              @case ('radio') {
                <div
                  class="flex flex-col gap-3"
                  role="radiogroup"
                  [attr.aria-label]="row.display || 'Response choices'"
                >
                  @for (option of options(); track option.value) {
                    <div
                      class="flex min-h-[56px] items-center rounded-xl border px-4 py-3"
                      [class.border-blue-200]="isSelected(rowIndex, option.value)"
                      [class.bg-blue-50]="isSelected(rowIndex, option.value)"
                      [class.border-gray-200]="!isSelected(rowIndex, option.value)"
                      [class.bg-white]="!isSelected(rowIndex, option.value)"
                    >
                      <lib-signal-radio
                        [groupName]="'single-choice-grid-mobile-' + rowIndex"
                        [optionValue]="option.value"
                        [value]="row.selectedChoiceId"
                        [label]="option.label"
                        [disabled]="disabled()"
                        [readonly]="readonly()"
                        [size]="'md'"
                        [id]="'single-choice-grid-mobile-radio-' + rowIndex + '-' + option.value"
                        (valueChange)="selectOption(rowIndex, $event)"
                      />
                    </div>
                  }
                </div>
              }

              @case ('button') {
                <div
                  class="grid grid-cols-2 gap-2"
                  role="radiogroup"
                  [attr.aria-label]="row.display || 'Response choices'"
                >
                  @for (option of options(); track option.value) {
                    <button
                      type="button"
                      class="min-h-9 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                      [class.border-blue-600]="isSelected(rowIndex, option.value)"
                      [class.bg-blue-600]="isSelected(rowIndex, option.value)"
                      [class.text-white]="isSelected(rowIndex, option.value)"
                      [class.border-gray-300]="!isSelected(rowIndex, option.value)"
                      [class.bg-white]="!isSelected(rowIndex, option.value)"
                      [class.text-gray-900]="!isSelected(rowIndex, option.value)"
                      [disabled]="disabled() || readonly()"
                      [attr.aria-checked]="isSelected(rowIndex, option.value)"
                      role="radio"
                      (click)="selectOption(rowIndex, option.value)"
                    >
                      {{ option.label }}
                    </button>
                  }
                </div>
              }

              @case ('dropdown') {
                <div class="w-full min-w-0">
                  <lib-signal-dropdown
                    [items]="options()"
                    hint="Select"
                    valuePath="value"
                    displayPath="label"
                    [showClearButton]="false"
                    [value]="row.selectedChoiceId"
                    [disabled]="disabled() || readonly()"
                    (valueChange)="selectOption(rowIndex, $event)"
                  />
                </div>
              }
            }
          </div>
        </section>
      }
    </div>

    <!-- Desktop table layout -->
    <div
      class="w-full overflow-x-auto rounded-lg border border-gray-200"
      [class.hidden]="previewMode() !== 'desktop'"
    >
      <table class="w-full min-w-[420px] table-fixed border-collapse text-sm">
        <thead class="bg-slate-100">
        <tr>
          <th
            class="w-[42%] border-b border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700"
          >
            Item
          </th>

          @if (effectiveType() === 'dropdown') {
            <th
              class="border-b border-gray-200 px-3 py-3 text-left font-semibold text-gray-700"
            >
              Response
            </th>
          } @else {
            @for (option of options(); track option.value) {
              <th
                class="border-b border-r border-gray-200 px-3 py-3 text-center font-semibold text-gray-700 last:border-r-0"
              >
                {{ option.label }}
              </th>
            }
          }
        </tr>
        </thead>

        <tbody>
          @for (row of rows(); track $index; let rowIndex = $index) {
            <tr class="border-b border-gray-100 last:border-b-0">
              <!-- Item cell -->
              <td class="border-r border-gray-200 px-4 py-3">
                @if (isUserAddedRowIndex(rowIndex)) {
                  <div class="flex items-center gap-2">
                    <input
                      type="text"
                      class="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      [value]="row.display"
                      [disabled]="disabled() || readonly()"
                      placeholder="Enter item"
                      (input)="updateDisplay(rowIndex, $any($event.target).value)"
                      (blur)="markLocallyTouched()"
                    />

                    @if (canRemoveRow(rowIndex)) {
                      <button
                        type="button"
                        class="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-60"
                        [disabled]="disabled() || readonly()"
                        aria-label="Remove row"
                        (click)="removeRow(rowIndex)"
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    }
                  </div>
                } @else {
                  <span class="font-medium text-gray-900">
                    {{ row.display }}
                  </span>
                }
              </td>

              <!-- Dropdown: one response cell spanning the row's response area -->
              @if (effectiveType() === 'dropdown') {
                <td class="px-3 py-3">
                  <div class="w-full min-w-0">
                    <lib-signal-dropdown
                      [items]="options()"
                      hint="Select"
                      valuePath="value"
                      displayPath="label"
                      [showClearButton]="false"
                      [value]="row.selectedChoiceId"
                      [disabled]="disabled() || readonly()"
                      (valueChange)="selectOption(rowIndex, $event)"
                    />
                  </div>
                </td>
              } @else {
                <!-- Radio and button: one cell per option -->
                @for (option of options(); track option.value; let last = $last) {
                  <td
                    class="border-r border-gray-200 px-3 py-3 text-center"
                    [class.border-r-0]="last"
                  >
                    @switch (effectiveType()) {
                      @case ('radio') {
                        <div class="flex items-center justify-center">
                          <lib-signal-radio
                            [groupName]="'single-choice-grid-desktop-' + rowIndex"
                            [optionValue]="option.value"
                            [value]="row.selectedChoiceId"
                            [label]="''"
                            [disabled]="disabled()"
                            [readonly]="readonly()"
                            [size]="'md'"
                            [id]="'single-choice-grid-desktop-radio-' + rowIndex + '-' + option.value"
                            (valueChange)="selectOption(rowIndex, $event)"
                          />
                        </div>
                      }

                      @case ('button') {
                        <button
                          type="button"
                          class="w-full rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                          [class.border-blue-600]="isSelected(rowIndex, option.value)"
                          [class.bg-blue-600]="isSelected(rowIndex, option.value)"
                          [class.text-white]="isSelected(rowIndex, option.value)"
                          [class.border-gray-300]="!isSelected(rowIndex, option.value)"
                          [class.bg-gray-100]="!isSelected(rowIndex, option.value)"
                          [class.text-gray-900]="!isSelected(rowIndex, option.value)"
                          [disabled]="disabled() || readonly()"
                          [attr.aria-pressed]="isSelected(rowIndex, option.value)"
                          (click)="selectOption(rowIndex, option.value)"
                        >
                          {{ option.label }}
                        </button>
                      }
                    }
                  </td>
                }
              }
            </tr>
          }
        </tbody>
      </table>
    </div>

    @if (allowAdditionalRows()) {
      <button
        type="button"
        class="mt-1 flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-600 transition hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        [disabled]="disabled() || readonly()"
        (click)="addRow()"
      >
        {{ addRowButtonLabel() }}
      </button>
    }
  </div>
</lib-base-signal-input>
